import React, {
  FormEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Icon, Spinner } from '../../../atoms'

export type ProposalAdvisorConversationMessageFormState =
  | 'disabled'
  | 'sending'
  | 'ready'

export type ProposalAdvisorConversationMessageFormProps = Readonly<{
  state: ProposalAdvisorConversationMessageFormState
  onSubmit: (message: string) => void
}>

export type ProposalAdvisorConversationMessageFormHandle = Readonly<{
  reset: () => void
}>

export const ProposalAdvisorConversationMessageForm = forwardRef<
  ProposalAdvisorConversationMessageFormHandle,
  ProposalAdvisorConversationMessageFormProps
>(function ProposalAdvisorConversationMessageForm({ onSubmit, state }, ref) {
  const intl = useIntl()
  const inputRef = useRef<null | HTMLInputElement>(null)
  const [content, setContent] = useState<string>('')

  useImperativeHandle(
    ref,
    () => ({
      reset: () => (inputRef.current!.value = ''),
    }),
    [],
  )

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit(content)
  }

  const isValidInput = () => {
    return content.length > 0
  }

  return (
    <form
      className='flex flex-row items-center gap-3 px-5 py-3'
      onSubmit={handleSubmit}
    >
      <input
        readOnly={['sending', 'disabled'].includes(state)}
        ref={inputRef}
        onChange={(event) => setContent(event.target.value.trim())}
        className='h-8 flex-grow rounded-full bg-grey-300 px-3 py-2 read-only:opacity-80'
        placeholder={intl.formatMessage({
          defaultMessage: 'Entrez un message',
          id: 'ui.ProposalAdvisorConversationMessageForm.enterAMessage',
        })}
      />
      <button
        type='submit'
        className='text-grey-700 hover:text-grey-800 disabled:text-grey-800'
        disabled={['disabled', 'sending'].includes(state) || !isValidInput()}
      >
        <span className='sr-only'>
          <FormattedMessage
            defaultMessage='Envoyer le message'
            id='ui.ProposalAdvisorConversationMessageForm.sendMessage'
          />
        </span>
        {state === 'sending' ? (
          <Spinner width={25} height={25} color='grey' />
        ) : (
          <Icon size={25} name='send' />
        )}
      </button>
    </form>
  )
})
